import { api } from "@common/api";
import { timeAgo } from "@jkast90/kast-common/format";
import type { Feed, GameSummary, UserSummary } from "../types/api";

// The games-and-players feed from play-api's /dashboard.

// The shapes are the API's (src/types/api.ts, generated from api/openapi.json).
export type { Feed, GamePlayer, GameSaves, GameSummary, Picture, UserSummary } from "../types/api";

export async function getDashboard(): Promise<Feed> {
  return api.get("/dashboard");
}

/** How a player is shown: their display name, else the part of their email before the @, else their account id. */
export function playerName(player: { sub: string; email?: string | null; name?: string | null }): string {
  return player.name || (player.email ?? `Account ${player.sub.slice(0, 8)}`).split("@")[0];
}

/** The games with the most time on them across every player, longest first. */
/** The game whose cover is a player's picture: their choice, else their most played. */
export function pictureGame(player: UserSummary): string | null {
  const cover = player.picture?.kind === "cover" ? player.picture.gameId : null;
  const chosen = cover && player.played.find((g) => g.game === cover);
  const most = [...player.played].sort((a, b) => b.seconds - a.seconds)[0];
  return (chosen || most)?.name ?? null;
}

/**
 * A player's picture, as the player card and profile page show it: their pool
 * picture, else the cover of the game they chose, else of their most played.
 * Null when there is none (no feed, unknown player, nothing played).
 */
export function playerPicture(
  feed: Feed | undefined,
  sub: string | null,
): { kind: "pool"; url: string } | { kind: "cover"; game: string } | null {
  const player = sub ? feed?.players.find((p) => p.sub === sub) : undefined;
  if (!player) return null;
  if (player.picture?.kind === "pool") return { kind: "pool", url: player.picture.url };
  const game = pictureGame(player);
  return game ? { kind: "cover", game } : null;
}

export function topGames(feed: Feed, count: number): GameSummary[] {
  return feed.games
    .filter((game) => game.seconds > 0)
    .sort((a, b) => b.seconds - a.seconds)
    .slice(0, count);
}

/** The players with the most time played, longest first. */
export function topPlayers(feed: Feed, count: number): UserSummary[] {
  return feed.players
    .filter((player) => player.secondsPlayed > 0)
    .sort((a, b) => b.secondsPlayed - a.secondsPlayed)
    .slice(0, count);
}

/** A player's most recent activity, play or save. */
export function lastActive(user: UserSummary): string | null {
  return [user.lastPlayed, user.lastSave].filter(Boolean).sort().pop() ?? null;
}

/** "4m ago", "2h ago", "3d ago", or the date past a week (timeAgo, narrow). */
export const ago = (iso: string) => timeAgo(iso, Date.now(), { maxUnit: "day", style: "narrow", numeric: "always" });
